/**
 * Тур гида и уточняющий вопрос (agroai-6al.23).
 *
 * Тур — план, который сервер ПРОВЕРИЛ по реестру (`ui_tour_plan`): шаги со
 * страницей, адресом, блоками и текстом. Панель рисует степпер «Шаг 2 из 6 ·
 * Назад / Дальше»; переход и подсветка — только по нажатию участника,
 * автоматически ничего не переключается («процессом управляю я сам»).
 * Пока тур открыт, снимок страницы несёт `tour` — вопрос на любом шаге
 * модель видит в контексте этого шага; тур можно продолжить.
 *
 * Вопрос с вариантами (`ui_ask_choice`) — кнопки; нажатие отправляет вариант
 * следующим сообщением, после ответа кнопки неактивны.
 */

import { ChevronLeft, ChevronRight, Compass, MessageCircleQuestionMark, Route, X } from 'lucide-react';
import { Icon } from '../astra/ui/Icon';
import { firstSentence } from '../ai-surface/surfaceKey';
import { surfaceText, surfaceTextOrNull } from '../ai-surface/surfaceText';
import { defineMessages } from '../i18n/define';
import { useT } from '../i18n/hooks';
import { isSourceLocale } from '../i18n/runtime';
import { srv, srvOrNull } from '../i18n/serverText';
import { screenTitle } from './screenTitles';

const m = defineMessages({
  idleEyebrow: { id: 'ai.tour.idleEyebrow', defaultMessage: 'Тур · {count, plural, one {# шаг} few {# шага} many {# шагов} other {# шага}}', description: 'Малая надпись в карточке тура до его запуска (ответ ИИ-гида); # — число шагов' },
  start: { id: 'ai.tour.start', defaultMessage: 'Пройти тур', description: 'Кнопка в карточке тура: начать прохождение тура по экранам' },
  aria: { id: 'ai.tour.aria', defaultMessage: 'Тур: {title}', description: 'Подпись для диктора карточки запущенного тура; {title} — название тура' },
  eyebrow: { id: 'ai.tour.eyebrow', defaultMessage: 'Тур по экранам', description: 'Малая надпись в карточке запущенного тура' },
  endTour: { id: 'ai.tour.endTour', defaultMessage: 'Закончить тур', description: 'Подпись и подсказка кнопки «×» в карточке запущенного тура' },
  stepsAria: { id: 'ai.tour.stepsAria', defaultMessage: 'Шаги тура', description: 'Подпись для диктора списка шагов тура' },
  counter: { id: 'ai.tour.counter', defaultMessage: 'Шаг {n} из {total} · {page}', description: 'Счётчик текущего шага тура; {n} — номер шага, {total} — всего шагов, {page} — название экрана шага' },
  quotedRegion: { id: 'ai.tour.quotedRegion', defaultMessage: '«{title}»', description: 'Название блока экрана в кавычках, жирным, в списке блоков шага тура; {title} — название блока' },
  missing: { id: 'ai.tour.missing', defaultMessage: 'Сейчас не на экране: откройте нужную вкладку или раскройте блок.', description: 'Пометка у блока шага тура, которого сейчас нет на экране' },
  back: { id: 'ai.tour.back', defaultMessage: 'Назад', description: 'Кнопка тура: предыдущий шаг; рядом стрелка' },
  startOpen: { id: 'ai.tour.startOpen', defaultMessage: 'Начать: открыть экран', description: 'Кнопка тура на первом шаге: открыть экран первого шага' },
  openScreen: { id: 'ai.tour.openScreen', defaultMessage: 'Открыть экран', description: 'Кнопка тура: открыть экран текущего шага' },
  finish: { id: 'ai.tour.finish', defaultMessage: 'Завершить', description: 'Кнопка тура на последнем шаге: закончить тур' },
  next: { id: 'ai.tour.next', defaultMessage: 'Дальше', description: 'Кнопка тура: следующий шаг; рядом стрелка' },
  barAria: { id: 'ai.tour.barAria', defaultMessage: 'Тур продолжается', description: 'Подпись для диктора полоски над полем ввода: тур продолжается' },
  barText: { id: 'ai.tour.barText', defaultMessage: 'Шаг {n} из {total}: {title}', description: 'Текст полоски над полем ввода; {n}/{total} — номер шага и всего, {title} — название шага' },
  prevStep: { id: 'ai.tour.prevStep', defaultMessage: 'Предыдущий шаг', description: 'Подпись для диктора кнопки-стрелки на полоске тура' },
  nextStep: { id: 'ai.tour.nextStep', defaultMessage: 'Следующий шаг', description: 'Подпись для диктора кнопки-стрелки на полоске тура' },
  finishTour: { id: 'ai.tour.finishTour', defaultMessage: 'Завершить тур', description: 'Подпись для диктора кнопки на полоске тура на последнем шаге' },
  defaultTitle: { id: 'ai.tour.defaultTitle', defaultMessage: 'Тур по платформе', description: 'Название обзорного тура гида по пунктам меню, когда ИИ не дал своего названия (сервер, ui_tools.tour_plan)' },
});

/** Название тура: умолчание сервера («Тур по платформе») — на языке интерфейса, название модели — как есть. */
function tourTitle(tour: TourPlan, t: ReturnType<typeof useT>): string {
  return tour.title === m.defaultTitle.defaultMessage ? t(m.defaultTitle) : tour.title;
}

/** Первая фраза описания блока на языке интерфейса; перевода нет — `null` (русский текст в узбекском шаге не показывается). */
function regionLead(region: TourRegion): string | null {
  return surfaceTextOrNull('lead', firstSentence(region.shows));
}

export interface TourRegion {
  id: string;
  title: string;
  shows: string;
}

export interface TourStep {
  n: number;
  page: string;
  page_title: string;
  href: string;
  regions: TourRegion[];
  title: string;
  text: string;
  /** Живые числа шага — по ссылке на метрику блока каталога. */
  numbers?: { region: string; metric: string; label: string; unit?: string | null; definition?: string | null; value: string }[];
}

export interface TourPlan {
  kind: 'tour';
  title: string;
  intro?: string | null;
  steps: TourStep[];
}

export interface ChoiceQuestion {
  kind: 'choice';
  question: string;
  options: string[];
}

export const isTour = (value: unknown): value is TourPlan =>
  typeof value === 'object' &&
  value !== null &&
  (value as TourPlan).kind === 'tour' &&
  Array.isArray((value as TourPlan).steps) &&
  (value as TourPlan).steps.length > 0;

export const isChoice = (value: unknown): value is ChoiceQuestion =>
  typeof value === 'object' &&
  value !== null &&
  (value as ChoiceQuestion).kind === 'choice' &&
  Array.isArray((value as ChoiceQuestion).options);

/** Где участник в туре: какой шаг выбран и открыт ли он на экране. */
export interface TourPosition {
  id: string;
  step: number;
  opened: boolean;
  /** Блоки шага, которых на экране не нашлось (другая вкладка, свёрнуто). */
  missing: string[];
}

interface TourCardProps {
  tour: TourPlan;
  /** Позиция, если этот тур сейчас активен. */
  position: TourPosition | null;
  onGo: (step: number) => void;
  onClose: () => void;
}

export function TourCard({ tour, position, onGo, onClose }: TourCardProps) {
  const t = useT();
  const total = tour.steps.length;
  if (!position) {
    return (
      <div className="agent-tour agent-tour--idle" data-tool="ui_tour_plan">
        <div className="agent-tour__head">
          <span className="agent-tour__eyebrow">
            <Icon icon={Route} size={16} /> {t(m.idleEyebrow, { count: total })}
          </span>
          <span className="agent-tour__title">{tourTitle(tour, t)}</span>
        </div>
        <button type="button" className="btn btn--sm" onClick={() => onGo(0)}>
          {t(m.start)}
        </button>
      </div>
    );
  }
  const index = Math.min(position.step, total - 1);
  const step = tour.steps[index]!;
  const last = index === total - 1;
  return (
    <div className="agent-tour" data-tool="ui_tour_plan" aria-label={t(m.aria, { title: tourTitle(tour, t) })}>
      <div className="agent-tour__head">
        <span className="agent-tour__eyebrow">
          <Icon icon={Route} size={16} /> {t(m.eyebrow)}
        </span>
        <span className="agent-tour__title">{tourTitle(tour, t)}</span>
        {tour.intro ? <p className="agent-tour__intro">{tour.intro}</p> : null}
        <button type="button" className="agent-tour__close focus-ring" onClick={onClose} aria-label={t(m.endTour)} title={t(m.endTour)}>
          <Icon icon={X} size={16} />
        </button>
      </div>

      <ol className="agent-tour__steps" aria-label={t(m.stepsAria)}>
        {tour.steps.map((item, i) => (
          <li key={item.n}>
            <button
              type="button"
              className={`agent-tour__stepbtn focus-ring${i === index ? ' is-current' : ''}${i < index ? ' is-done' : ''}`}
              aria-current={i === index ? 'step' : undefined}
              onClick={() => onGo(i)}
            >
              <span className="agent-tour__num">{i + 1}</span>
              <span className="agent-tour__steptitle">{surfaceText('menu', item.title)}</span>
            </button>
          </li>
        ))}
      </ol>

      <section className="agent-tour__current" aria-live="polite">
        <div className="agent-tour__counter">
          {t(m.counter, { n: index + 1, total, page: screenTitle(step.page, step.page_title) })}
        </div>
        <h4 className="agent-tour__h">{surfaceText('menu', step.title)}</h4>
        <p className="agent-tour__text">{surfaceText('purpose', step.text)}</p>
        {step.numbers?.length ? (
          <dl className="agent-tour__numbers">
            {step.numbers.map((item) => (
              <div
                key={`${item.region}:${item.metric}`}
                title={(isSourceLocale() ? item.definition : srvOrNull(item.definition)) ?? undefined}
              >
                <dt>{srv(item.label)}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {step.regions.length ? (
          <ul className="agent-tour__regions">
            {step.regions.map((region) => {
              const lead = regionLead(region);
              return (
                <li key={region.id} className={position.opened && position.missing.includes(region.id) ? 'is-missing' : undefined}>
                  <strong>{t(m.quotedRegion, { title: surfaceText('title', region.title) })}</strong>
                  {lead ? (
                    <>
                      {' — '}
                      {/* Полное описание — подсказкой только на языке-источнике: перевода у него нет. */}
                      <span title={isSourceLocale() ? region.shows : undefined}>{lead}</span>
                    </>
                  ) : null}
                  {position.opened && position.missing.includes(region.id) ? (
                    <span className="agent-tour__miss"> {t(m.missing)}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      <div className="agent-tour__nav">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onGo(index - 1)} disabled={index === 0}>
          <Icon icon={ChevronLeft} size={16} /> {t(m.back)}
        </button>
        {!position.opened ? (
          <button type="button" className="btn btn--sm" onClick={() => onGo(index)}>
            <Icon icon={Compass} size={16} /> {index === 0 ? t(m.startOpen) : t(m.openScreen)}
          </button>
        ) : null}
        {last ? (
          <button type="button" className="btn btn--sm" onClick={onClose}>
            {t(m.finish)}
          </button>
        ) : (
          <button type="button" className="btn btn--sm" onClick={() => onGo(index + 1)}>
            {t(m.next)} <Icon icon={ChevronRight} size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Полоска над полем ввода: тур продолжается, пока участник задаёт вопросы. */
export function TourBar({ tour, position, onGo, onClose }: TourCardProps & { position: TourPosition }) {
  const t = useT();
  const total = tour.steps.length;
  const index = Math.min(position.step, total - 1);
  return (
    <div className="agent-tourbar" role="group" aria-label={t(m.barAria)}>
      <Icon icon={Route} size={16} />
      <span className="agent-tourbar__text">
        {t(m.barText, { n: index + 1, total, title: surfaceText('menu', tour.steps[index]?.title ?? '') })}
      </span>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => onGo(index - 1)} disabled={index === 0} aria-label={t(m.prevStep)}>
        <Icon icon={ChevronLeft} size={16} />
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => (index === total - 1 ? onClose() : onGo(index + 1))}
        aria-label={index === total - 1 ? t(m.finishTour) : t(m.nextStep)}
      >
        <Icon icon={index === total - 1 ? X : ChevronRight} size={16} />
      </button>
    </div>
  );
}

interface ChoiceCardProps {
  choice: ChoiceQuestion;
  /** Текст ответа участника, если он уже ответил (следующее сообщение). */
  answered: string | null;
  disabled: boolean;
  onPick: (option: string) => void;
}

export function ChoiceCard({ choice, answered, disabled, onPick }: ChoiceCardProps) {
  return (
    <div className="agent-choice" data-tool="ui_ask_choice">
      <p className="agent-choice__q">
        <Icon icon={MessageCircleQuestionMark} size={16} /> {choice.question}
      </p>
      <div className="agent-choice__options" role="group" aria-label={choice.question}>
        {choice.options.map((option) => (
          <button
            key={option}
            type="button"
            className={`agent-chip agent-choice__opt focus-ring${answered === option ? ' is-picked' : ''}`}
            aria-pressed={answered === option}
            disabled={answered !== null || disabled}
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}
